import { Coins, Loader2, Minus, Share2, ShieldCheck, TrendingDown, TrendingUp, Trophy, Users, Zap } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getProfitSummary } from '@/api/member';
import { fmtUsdAmount as fmtUsd } from '@/lib/money';

// Rich modern semantic colors (NOT raw #FF0000 / #00FF00).
const COLOR_PROFIT = '#10B981'; // emerald 500
const COLOR_LOSS = '#EF4444'; // red 500
const COLOR_NEUTRAL = '#FFC20E'; // Steelers gold

const signedFmt = (raw) => {
  const n = Number(raw || 0);
  if (n > 0) return `+$${fmtUsd(n)}`;
  if (n < 0) return `-$${fmtUsd(Math.abs(n))}`;
  return `$0.00`;
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div
    className="rounded-xl p-3 md:p-4 border"
    style={{
      background: 'rgba(0,0,0,0.32)',
      borderColor: 'rgba(255, 182, 18, 0.18)',
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
      <Icon className="w-3.5 h-3.5 text-[#FFC20E]" />
      {label}
    </div>
    <div className="sc-tabular font-display font-bold text-xl md:text-2xl mt-1.5 leading-none sc-gold-text" data-testid={`${testid}-value`}>
      ${fmtUsd(value)}
    </div>
  </div>
);

const ProfitBadge = ({ me }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const r = await getProfitSummary();
        if (!alive) return;
        setData(r);
        setErrored(false);
      } catch {
        if (!alive) return;
        setErrored(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    probe();
    // Poll every 60s (slow-poll, 2025-06): the dashboard "fruits of labor"
    // view doesn't need sub-minute freshness because the cycle engine ticks
    // at ~60s anyway. Page refresh (F5) always gets instant fresh data.
    const t = setInterval(probe, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const handleShare = useCallback(async () => {
    if (!data) return;
    setSharing(true);

    const sign = data.is_profit_positive ? '+' : data.is_break_even ? '' : '-';
    const profitAbs = data.is_break_even ? '0.00' : fmtUsd(Math.abs(Number(data.net_profit || 0)));

    const text =
      `1SimpleCycler 3.0 - Live Stats\n` +
      `Member: @${me?.username || 'anon'}\n` +
      `------------------------------\n` +
      `Total Deposited:  $${fmtUsd(data.deposits_total)}\n` +
      `Personal Cycle:   $${fmtUsd(data.cycle_earnings)}\n` +
      `Matching Bonus:   $${fmtUsd(data.match_earnings)}\n` +
      `Total Earnings:   $${fmtUsd(data.total_earnings)}\n` +
      `------------------------------\n` +
      `Net Profit:       ${sign}$${profitAbs}\n` +
      `\n` +
      `Join me - 1simplecycler3.com`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: '1SimpleCycler 3.0 - My Stats',
          text,
        });
        setSharing(false);
        return;
      }
    } catch {
      // user-canceled / unsupported -> fall through to clipboard
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success('Stats copied to clipboard - paste anywhere to share');
    } catch {
      toast.error('Could not copy stats - long-press to select instead');
    } finally {
      setSharing(false);
    }
  }, [data, me]);

  // ---- Loading skeleton ------------------------------------------------
  if (loading && !data) {
    return (
      <section className="sc-glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden" data-testid="profit-badge-loading">
        <div className="flex items-center gap-3 text-[var(--sc-text-mute)] text-[12px] uppercase tracking-[0.22em]">
          <Loader2 className="w-4 h-4 animate-spin text-[#FFC20E]" />
          Calculating your profit snapshot…
        </div>
      </section>
    );
  }

  // ---- Error fallback (rare — keeps dashboard usable) ------------------
  if (errored || !data) {
    return (
      <section className="sc-glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden" data-testid="profit-badge-error">
        <div className="text-[12px] text-[var(--sc-text-mute)]">Profit snapshot temporarily unavailable. Reconnecting…</div>
      </section>
    );
  }

  // ---- Resolve display state -------------------------------------------
  const isPositive = !!data.is_profit_positive;
  const isBreakEven = !!data.is_break_even;
  const profitColor = isPositive ? COLOR_PROFIT : isBreakEven ? COLOR_NEUTRAL : COLOR_LOSS;
  const ProfitIcon = isPositive ? TrendingUp : isBreakEven ? Minus : TrendingDown;
  const subLabel = isPositive ? 'In Profit' : isBreakEven ? 'Break-even' : 'Building';

  const signedProfit = signedFmt(data.net_profit);

  return (
    <section
      className="sc-glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden"
      style={{
        border: '1px solid rgba(255, 182, 18, 0.42)',
        boxShadow: '0 0 0 1px rgba(255,182,18,0.10), 0 30px 80px -20px rgba(255,182,18,0.35)',
      }}
      data-testid="profit-badge"
    >
      {/* Decorative gold radial — well under 20% viewport per design rule */}
      <div
        className="absolute -top-24 -right-24 w-72 h-72 pointer-events-none rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(255,182,18,0.22), transparent 65%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-24 -left-24 w-72 h-72 pointer-events-none rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(255,182,18,0.10), transparent 65%)',
        }}
        aria-hidden="true"
      />

      {/* Top strip: brand + username + live + share */}
      <div className="relative z-10 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: 'linear-gradient(135deg, #FFC20E, #FFB612)',
            }}
            aria-hidden="true"
          >
            <ShieldCheck className="w-4 h-4 text-[#0a0a0b]" strokeWidth={2.6} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-sm whitespace-nowrap" data-testid="profit-badge-brand">
              1Simple<span className="sc-gold-text">Cycler</span>
              <span className="text-[var(--sc-text-mute)] text-[11px] ml-1">3.0</span>
            </div>
            <div className="text-[11px] text-[var(--sc-text-dim)] truncate" data-testid="profit-badge-username">
              @{me?.username || 'member'} · Profit Snapshot
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border"
            style={{
              color: '#FFE08A',
              background: 'rgba(255,182,18,0.10)',
              borderColor: 'rgba(255,182,18,0.32)',
            }}
            data-testid="profit-badge-live"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFC20E] animate-pulse" aria-hidden="true" />
            Live
          </span>
          <button
            type="button"
            onClick={handleShare}
            disabled={sharing}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border transition disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              color: '#0a0a0b',
              background: '#FFFFFF',
              borderColor: 'rgba(255, 194, 14, 0.55)',
            }}
            aria-label="Share my profit snapshot"
            data-testid="profit-badge-share"
          >
            {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
            Share
          </button>
        </div>
      </div>

      {/* Hero: NET PROFIT */}
      <div className="relative z-10 mt-6 md:mt-7 text-center">
        <div className="text-[10px] uppercase tracking-[0.30em] text-[var(--sc-text-mute)]">Net Profit</div>
        <div
          className="sc-tabular font-display font-extrabold mt-2 leading-none flex items-center justify-center gap-3"
          style={{
            color: profitColor,
            fontSize: 'clamp(40px, 7vw, 64px)',
            textShadow: `0 0 24px ${profitColor}33`,
          }}
          data-testid="profit-badge-net-value"
        >
          <ProfitIcon
            className="shrink-0"
            style={{
              width: '0.7em',
              height: '0.7em',
            }}
            strokeWidth={2.4}
            aria-hidden="true"
          />
          <span>{signedProfit}</span>
        </div>
        <div
          className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] px-3 py-1 rounded-full border"
          style={{
            color: profitColor,
            borderColor: `${profitColor}55`,
            background: `${profitColor}14`,
          }}
          data-testid="profit-badge-sub-label"
        >
          {isPositive ? (
            <Trophy className="w-3.5 h-3.5" aria-hidden="true" />
          ) : isBreakEven ? (
            <Minus className="w-3.5 h-3.5" aria-hidden="true" />
          ) : (
            <TrendingDown className="w-3.5 h-3.5" aria-hidden="true" />
          )}
          {subLabel}
        </div>
        <p className="mt-3 text-[12px] text-[var(--sc-text-dim)] max-w-xl mx-auto leading-snug">
          Lifetime physical crypto deposited vs. lifetime gross earnings (cycle + matching bonus). Withdrawals and current balances are intentionally excluded.
        </p>
      </div>

      {/* 4-stat strip */}
      <div className="relative z-10 mt-6 md:mt-7 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Stat icon={Coins} label="Total Deposited" value={data.deposits_total} testid="profit-stat-deposited" />
        <Stat icon={Zap} label="Personal Cycle" value={data.cycle_earnings} testid="profit-stat-cycle" />
        <Stat icon={Users} label="Matching Bonus" value={data.match_earnings} testid="profit-stat-match" />
        <Stat icon={Trophy} label="Total Earnings" value={data.total_earnings} testid="profit-stat-earnings" />
      </div>
    </section>
  );
};

export default ProfitBadge;
